// Which country pages a search engine is asked to index.
//
// The country table ($lib/seo/countries) generates one page per jurisdiction,
// and a page generated from one row differs from its neighbour only by the
// row's own values. A search engine judges a set like that as a whole: a few
// hundred pages that say the same thing in the same order read as a set built
// to catch searches, and the doubt that earns is applied to the rest of the
// domain rather than to those pages alone.
//
// So only the pages that carry facts of their own are indexed: the ones in the
// list below, each of which also has an entry in ./country-facts. The rest keep
// answering 200 with `noindex, follow` – a visitor holding the address still
// reaches a working page, and a crawler that fetches it learns to drop it and
// still follows its links. They are left out of the sitemap, so a deploy never
// announces them again, and no page of this site links to them.
//
// Two rules keep the list honest, and the unit suite beside this file checks
// both: it never grows past MAX_INDEXED_COUNTRY_PAGES, and every slug in it has
// facts. The build guard `scripts/assert-pages.mjs` checks the same limit over
// what the build actually emitted.
//
// The market pages the translated languages carry ($lib/i18n/registry) are in
// this list wherever facts could be sourced for them. A translated country page
// follows its English page's decision either way: one outside the list stays
// online with the same `noindex, follow` and out of the sitemap.

/** The ceiling. Raising it is a decision about the whole site, not a code change. */
export const MAX_INDEXED_COUNTRY_PAGES = 25;

/**
 * The indexed country pages, by slug. Chosen by measured search demand for a
 * country's passport and by the markets the translated pages serve.
 */
export const INDEXED_COUNTRY_SLUGS: readonly string[] = [
  "australia",
  "bangladesh",
  "brazil",
  "china",
  "france",
  "germany",
  "india",
  "indonesia",
  "italy",
  "japan",
  "kenya",
  "mexico",
  "nigeria",
  "philippines",
  "south-africa",
  "south-korea",
  "spain",
  "turkey",
  "united-arab-emirates",
  "united-kingdom",
  "united-states",
  "vietnam",
];

const INDEXED = new Set(INDEXED_COUNTRY_SLUGS);

/** Whether the country page with this slug is meant to be in a search index. */
export function isIndexedCountry(slug: string): boolean {
  return INDEXED.has(slug);
}
